import { Injectable } from '@angular/core';
import moment from 'moment';
import { BadgeUpdate } from '@weavix/models/src/badges/badge-update';
import { Person } from '../models/person.model';
import { Topic } from '@weavix/models/src/topic/topic';
import { WaltStatus } from '../models/walt.model';
import { Walt, WaltBulkUpdateRequest, WaltModel, WaltResponse, WaltSessionsResponse } from '@weavix/models/src/walt/walt';
import { WaltSoundStatusEvent } from '@weavix/models/src/walt/walt-sound-status';
import { MapWalt } from '../models/weavix-map.model';
import { AccountService } from './account.service';
import { HttpService } from './http.service';
import { PersonService } from './person.service';
import { PubSubService } from './pub-sub.service';
import { TranslationService } from './translation.service';
import { WaltConnectionLog } from '@weavix/models/src/walt/walt-connection-log';
import { LatestVersion } from '@weavix/models/src/api/version-response';
import { FeatureId } from '@weavix/models/src/features/feature';
import { FeatureFlagService } from './feature-flag.service';
import { distinctUntilChanged, Subscription, tap } from 'rxjs';
import { FacilityService } from './facility.service';
import { WaltSettings } from '@weavix/models/src/facility/facility';
import { isVersionNewer } from '@weavix/domain/src/utils/is-version-newer';

@Injectable({
    providedIn: 'root',
})
export class WaltService {
    constructor(
        private readonly personService: PersonService,
        private readonly httpService: HttpService,
        private readonly pubSubService: PubSubService,
        private readonly accountService: AccountService,
        private readonly translationService: TranslationService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly facilityService: FacilityService,
    ) {
        this.subscribeToFacilityChanges();
    }

    private subscribeToFacilityChanges() {
        this.facilityService.currentFacility$.pipe(
            distinctUntilChanged(),
            tap(() => this.subscribeToDeviceManagementFlagChanges()),
        ).subscribe();
    }
    private deviceManagementSubscription: Subscription;
    /** Re-reads the enhancements flag, which counts as on when any of the user's sites has it on, not just the current site. */
    private subscribeToDeviceManagementFlagChanges() {
        this.deviceManagementSubscription?.unsubscribe();
        this.deviceManagementSubscription = this.featureFlagService.getFeatureFlag$(FeatureId.Walt2DeviceManagementEnhancements).pipe(
            distinctUntilChanged(),
            tap(enabled => this.isDeviceManagementEnhancementsEnabled = enabled),
        ).subscribe();
    }
    private isDeviceManagementEnhancementsEnabled: boolean = false;

    get deviceManagementEnhancementsEnabled(): boolean {
        return this.isDeviceManagementEnhancementsEnabled;
    }

    static cacheCollection = 'walt';

    static readonly ACTIVE_WALT_TIME_MINUTES = 10;
    static readonly RECENT_UPDATE_TIME_HOURS = 24;

    static url = (id?: string) => id ? `/track/walt/${id}` : `/track/walt`;

    static waltToBadgeUpdate(walt: MapWalt): BadgeUpdate {
        const personBadge = walt?.person?.badge;
        const waltLocation = { location: walt.location, level: walt.level };
        return personBadge ? { ...personBadge, ...waltLocation } : waltLocation;
    }

    static waltActive(walt: Walt, atTime?: number) {
        const activeTimeComparison: number = (atTime ?? Date.now()) - (this.ACTIVE_WALT_TIME_MINUTES * 60 * 1000);
        const lastLocationUpdateTimestampIsActive: boolean = new Date(walt.locationUpdated ?? 1).getTime() > activeTimeComparison;
        const lastUpdateTimeStampIsActive: boolean = new Date(walt.updated ?? 1).getTime() > activeTimeComparison;

        return lastLocationUpdateTimestampIsActive || lastUpdateTimeStampIsActive; // within active time
    }

    static isCurrentlyAssigned(walt: Walt): boolean {
        if (!walt.lastAssigned?.personId
            || new Date(walt.lastAssigned?.date).getTime() < new Date(walt.wrangler?.date).getTime()
            || (walt.wrangler && walt.wrangler?.id)) return false;
        else return true;
    }

    static getWaltStatus(walt: Walt, newWay?: boolean): WaltStatus {
        if (newWay) return WaltService.getWaltStatusNewWay(walt);
        else return WaltService.getWaltStatusOldWay(walt);
    }

    /**
     * The server time of the walt's most recent direct report, falling back to `updated` for
     * walts that predate `lastReportedAt`.
     */
    static getLastReportedAt(walt: Walt): Date | undefined {
        return walt.lastReportedAt ?? walt.updated;
    }

    /** `getLastReportedAt` as epoch milliseconds, or 0 when the walt has no report time at all. */
    static getLastReportedTimestamp(walt: Walt): number {
        return new Date(this.getLastReportedAt(walt) ?? 0).getTime();
    }

    /**
     * True when the walt's `refreshFailedAt` is newer than its most recent direct report,
     * meaning the last on-demand refresh did not reach it.
     */
    public static hasRefreshFailureAfterLatestUpdate(walt: Walt): boolean {
        const refreshFailedAt = new Date(walt.refreshFailedAt ?? 0).getTime();
        if (!refreshFailedAt || Number.isNaN(refreshFailedAt)) return false;
        return refreshFailedAt > this.getLastReportedTimestamp(walt);
    }

    private static getWaltStatusNewWay(walt: Walt): WaltStatus {
        if (walt.poweredOff) return WaltStatus.PoweredOff;

        if (walt.unreachable || this.hasRefreshFailureAfterLatestUpdate(walt)) return WaltStatus.NoConnection;

        const staleTimeComparison: number = Date.now() - (this.RECENT_UPDATE_TIME_HOURS * 60 * 60 * 1000);
        const lastUpdated = this.getLastReportedTimestamp(walt);
        if (lastUpdated < staleTimeComparison) return WaltStatus.NoConnection;
        if (walt.wrangler?.id) return WaltStatus.Docked;
        if (walt.loggedIn) return WaltStatus.Active;
        return WaltStatus.LoggedOut;
    }

    private static getWaltStatusOldWay(walt: Walt): WaltStatus {
        const assigned = this.isCurrentlyAssigned(walt);
        const active = this.waltActive(walt);
        if (walt.wrangler?.id) return WaltStatus.Docked;
        if (!assigned && (!active || walt.poweredOff)) return WaltStatus.NoConnectionUnassigned;
        if (!assigned) return WaltStatus.Unassigned;
        if (assigned && (!active || walt.poweredOff)) return WaltStatus.NoConnectionAssigned;
        if (assigned && !walt.loggedIn && !walt.poweredOff) return WaltStatus.PoweredOnLoggedOut;
        return WaltStatus.PoweredOnLoggedIn;
    }

    getWaltBatteryLevel(batteryLevel: number): string | null {
        if (batteryLevel === undefined || batteryLevel === null) return null;

        const batteryPercent = Math.max(0, Math.min(100, batteryLevel <= 1 ? batteryLevel * 100 : batteryLevel));

        if (batteryPercent > 75) return this.translationService.getImmediate('walt.battery-76-100');
        if (batteryPercent > 50) return this.translationService.getImmediate('walt.battery-51-75');
        if (batteryPercent > 25) return this.translationService.getImmediate('walt.battery-26-50');
        return this.translationService.getImmediate('walt.battery-0-25');
    }

    /**
     * Pulls latest walt version for the given walt model from the weavix API.
     */
    async getLatestWaltVersion(model: WaltModel | null, component: any = null): Promise<string> {
        if (!model) return '';
        const type = model === WaltModel.walt2 ? 'walt-ota-5g' : 'walt-ota-s';
        const latest = await this.httpService.get<LatestVersion>(component, '/downloads/latest', { type });

        const formattedVersion = latest.version?.replace(/\.zip$/i, '') ?? '';
        return formattedVersion;
    }

    /**
     * Version the walt must run: the global one, or the site override only when it is newer.
     * Same rule as the backend's mandatory-version lookup.
     */
    static getRequiredWaltVersion(model: WaltModel | null, latestByModel: Partial<Record<WaltModel, string>>, waltSettings?: WaltSettings): string {
        if (!model) return '';
        const globalVersion = latestByModel[model] ?? '';
        const override = (model === WaltModel.walt2 ? waltSettings?.overrideWaltOta5g : waltSettings?.overrideWaltOta)?.replace(/\.zip$/i, '');
        return override && isVersionNewer(globalVersion, override) ? override : globalVersion;
    }

    public getWaltLastUpdate(lastSync: Date | undefined): string {
        if (lastSync === undefined) return this.translationService.getImmediate('walt.not-recently-updated');

        const yesterdayTimeComparison: number = moment(new Date()).subtract(WaltService.RECENT_UPDATE_TIME_HOURS, 'hours').valueOf();
        const recentlyUpdated = new Date(lastSync).getTime() > yesterdayTimeComparison;

        return recentlyUpdated ? this.translationService.getImmediate('walt.recently-updated') : this.translationService.getImmediate('walt.not-recently-updated');
    }

    public isVersionOutdated(currentVersion: string, latestVersion: string): string {
        if (!currentVersion || !latestVersion) return this.translationService.getImmediate('walt.version-outdated');
        if (currentVersion === latestVersion) return this.translationService.getImmediate('walt.version-current');
        return this.translationService.getImmediate('walt.version-outdated');
    }

    getWranglerSlotString(walt: Walt): string {
        let name: string = '';
        if (!walt.wrangler?.id) return '-';
        if (walt.wrangler.id) {
            name += walt.wrangler.id;
            if (walt.wrangler.slot) name += ` ${this.translationService.getImmediate('walt.slot', { id: walt.wrangler.slot })}`;
        }

        return name;
    }

    public getWaltLastAssignedName(walt: MapWalt): string {
        if (!WaltService.isCurrentlyAssigned(walt as Walt)) return '-';

        return walt.person?.fullName
            ? walt.person?.fullName
            : walt.lastAssigned?.personName
                ? walt.lastAssigned?.personName
                : walt.lastAssigned.personId
                    ? this.translationService.getImmediate('generics.unknown')
                    : this.translationService.getImmediate('walt.unassigned');
    }

    public getWaltFacility(walt: WaltResponse): string {
        return walt.lastAssigned?.facilityName ?? this.translationService.getImmediate('walt.unassigned');
    }

    public async getWaltLastAssignedPerson(component, walt: Walt): Promise<Person> {
        if (walt.lastAssigned?.personId) return await this.personService.getPerson(component, walt.lastAssigned?.personId);
        else return null;
    }

    getAllByFacility(component: any, facilityId: string) {
        return this.httpService.get<WaltResponse[]>(component, `${WaltService.url()}/facility/${facilityId}`);
    }

    getAllByAccount(component: any, accountId: string) {
        return this.httpService.get<WaltResponse[]>(component, `${WaltService.url()}/account/${accountId}`);
    }

    async remoteLogout(component: any, id: string) {
        return this.httpService.post<void>(component, `${WaltService.url(id)}/logout`, {});
    }

    async playWaltSound(component: any, id: string) {
        return this.httpService.post<void>(component, `${WaltService.url(id)}/playSound`, {});
    }

    async stopWaltSound(component: any, id: string) {
        return this.httpService.post<void>(component, `${WaltService.url(id)}/playSound/stop`, {});
    }

    async markWaltUnreachable(component: any, id: string) {
        return this.httpService.post<void>(component, `${WaltService.url(id)}/playSound/unreachable`, {});
    }

    subscribeWaltSoundStatus(component: any, waltId: string) {
        return this.pubSubService.subscribe<WaltSoundStatusEvent>(component, Topic.AccountWaltSoundStatus, [this.accountService.getAccountId(), waltId]);
    }

    async setWaltFacility(component: any, waltid: string, facilityId: string) {
        return this.httpService.post<void>(component, `${WaltService.url(waltid)}/assign`, { facilityId });
    }

    async updateWaltCraft(component: any, waltId: string, craft: string | null) {
        return this.httpService.put<void>(component, `${WaltService.url(waltId)}/craft`, { craft });
    }

    async bulkUpdateWalts(component: any, request: WaltBulkUpdateRequest) {
        return this.httpService.post<Array<{ waltId: string, success: boolean }>>(
            component,
            `${WaltService.url()}/bulk-update`,
            request,
        );
    }

    async refreshWalt(component: any, waltId: string): Promise<WaltResponse> {
        return this.httpService.post<WaltResponse>(component, `${WaltService.url(waltId)}/refresh`, {});
    }

    async rebootWalt(component: unknown, waltId: string): Promise<void> {
        await this.httpService.post<void>(component, `${WaltService.url(waltId)}/reboot`, {});
    }

    async powerOffWalt(component: unknown, waltId: string): Promise<void> {
        await this.httpService.post<void>(component, `${WaltService.url(waltId)}/power-off`, {});
    }

    async installWaltUpdate(component: unknown, waltId: string): Promise<void> {
        await this.httpService.post<void>(component, `${WaltService.url(waltId)}/install-update`, {});
    }

    subscribeWaltUpdates(w: any) {
        return this.pubSubService.subscribe<WaltResponse>(w, Topic.AccountWaltUpdated, [this.accountService.getAccountId()]);
    }

    getWaltConnectionLogs(component: unknown, waltId: string) {
        return this.httpService.get<WaltConnectionLog[]>(component, `${WaltService.url(waltId)}/connection-logs`);
    }

    subscribeWaltConnectionLogs(component: unknown, waltId: string) {
        return this.pubSubService.subscribe<WaltConnectionLog>(component, Topic.WaltConnectionLogs, [waltId]);
    }

    async getWaltSessions(component: unknown, waltId: string, before: string | undefined, limit: number): Promise<WaltSessionsResponse> {
        return await this.httpService.get(
            component,
            `/track/events/walt/${waltId}/sessions`,
            { before, limit },
            null,
            null,
        ) ?? { sessions: [], nextCursor: undefined };
    }
}
